import {
  CLIENT_CONTEXT_FIELDS,
  ClientContextErrorCodes,
  ONBOARDING_EDIT_COLUMNS,
  hasClientIndustrySpaces,
  isClientContextColumn,
  resolveClientContextCustomerName,
  validateClientsParamsSetup,
  validateNewPartition,
  validateOnboardingCreate,
  type ClientsParamsSetupInput,
  type OnboardingCreateColumn,
  type OnboardingEditColumn,
} from "@nexus/contracts";
import {
  contractErrorFor,
  parseCustomerSetupDraft,
  type CustomerSetupDraft,
  type CustomerSetupErrors,
} from "./customer-setup";

// ─── Form layout ────────────────────────────────────────────────────
// Presentation for the Identity Hub onboarding form only. Storage, type and
// write rules stay in the contracts registry (`CLIENT_CONTEXT_FIELDS`).

export type OnboardingFormSectionId =
  | "essentials"
  | "positioning"
  | "focus"
  | "products"
  | "messaging"
  | "operations"
  | "competition"
  | "performance";

export interface OnboardingFormSection {
  id: OnboardingFormSectionId;
  columns: readonly OnboardingEditColumn[];
}

/**
 * Columns the form renders. `content_strategy` and `engagement` are still
 * stored and accepted by the onboarding edit route, but Product removed them
 * from data entry. Edits send only changed columns, so a stored value in a
 * column that is not rendered is never cleared.
 *
 * The essentials section is always open; every other section starts collapsed
 * so the form opens short.
 */
export const ONBOARDING_FORM_SECTIONS: readonly OnboardingFormSection[] = [
  { id: "essentials", columns: ["brand", "website_url", "premise", "target_audience", "focus_area"] },
  { id: "positioning", columns: ["differentiation", "challenges", "growth_target"] },
  { id: "focus", columns: ["non_focus", "product_to_focus_on", "additional_context_from_client"] },
  {
    id: "products",
    columns: [
      "product_description",
      "product_benefits",
      "product_variance",
      "best_sellers",
      "bundeling",
      "product_pricing_and_discounts",
      "promotions",
    ],
  },
  { id: "messaging", columns: ["tone_of_voice", "messaging_do", "messaging_dont", "things_to_avoid"] },
  { id: "operations", columns: ["logistic_support"] },
  { id: "competition", columns: ["known_competitors", "nemesis", "who_to_ignore", "inspiration_industry"] },
  { id: "performance", columns: ["stickrate_threshold"] },
];

export const ONBOARDING_ESSENTIALS_SECTION_ID: OnboardingFormSectionId = "essentials";

/** Single-line inputs; every other text column gets a textarea. */
export const ONBOARDING_SHORT_TEXT_COLUMNS: ReadonlySet<OnboardingEditColumn> = new Set([
  "brand",
  "website_url",
  "growth_target",
  "nemesis",
  "inspiration_industry",
]);

export type OnboardingInputKind = "text" | "json" | "numeric";

export function onboardingInputKind(column: OnboardingCreateColumn): OnboardingInputKind {
  const { type } = CLIENT_CONTEXT_FIELDS[column];
  if (type === "json") return "json";
  if (type === "numeric") return "numeric";
  return "text";
}

// ─── Draft ↔ stored values ──────────────────────────────────────────

/** Stickrate threshold Retool prefilled for a new client; the user can change or clear it. */
export const ONBOARDING_STICKRATE_THRESHOLD_DEFAULT = "50";

/** A new onboarding form. An existing record is never given these defaults. */
export function createOnboardingDraft(): OnboardingDraft {
  return { stickrate_threshold: ONBOARDING_STICKRATE_THRESHOLD_DEFAULT };
}

/**
 * JSON columns whose contract schema is a list of strings
 * (`stringListSchema` in `@nexus/contracts` `client-context/fields.ts`).
 * Every JSON column is edited as tags; these are the ones whose contract
 * rejects anything but a list of text, and get their own placeholder.
 */
const STRING_LIST_COLUMNS: ReadonlySet<OnboardingCreateColumn> = new Set(["product_to_focus_on"]);

export function isStringListColumn(column: OnboardingCreateColumn): boolean {
  return STRING_LIST_COLUMNS.has(column);
}

/**
 * A stored JSON value that is not a list of text, kept as the exact JSON so it
 * is shown read-only and never reshaped or sent unless the user replaces it
 * with a list. It is never typed in by the user.
 */
export interface OnboardingJsonDraft {
  json: string;
}

/**
 * Text and NUMERIC columns are strings. A JSON column follows the shape of its
 * stored value: a string list (or nothing) is a tag list, a stored text value
 * stays text, and any other stored JSON shape is kept as a read-only JSON draft
 * until replaced. Only `product_to_focus_on` is proven to be a list of text.
 */
export type OnboardingDraftValue = string | string[] | OnboardingJsonDraft;
export type OnboardingDraft = Partial<Record<OnboardingCreateColumn, OnboardingDraftValue>>;

export type OnboardingFieldError = "invalidList" | "invalidUrl" | "invalidNumber" | "invalid";
export type OnboardingFieldErrors = Partial<Record<OnboardingCreateColumn, OnboardingFieldError>>;

const JSON_INDENT = 2;

/** Seeds the form from a `client_context` read. */
export function onboardingDraftFromValues(values: Record<string, unknown>): OnboardingDraft {
  const draft: OnboardingDraft = {};
  for (const column of ONBOARDING_EDIT_COLUMNS) {
    draft[column] = toDraftValue(column, values[column]);
  }
  return draft;
}

function toDraftValue(column: OnboardingCreateColumn, value: unknown): OnboardingDraftValue {
  if (onboardingInputKind(column) === "json") {
    if (value === null || value === undefined) return [];
    if (isStringList(value)) return value;
    if (typeof value === "string" && !isStringListColumn(column)) return value;
    return { json: JSON.stringify(value, null, JSON_INDENT) };
  }
  if (value === null || value === undefined) return "";
  return String(value);
}

function isStringList(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((item) => typeof item === "string");
}

export function isOnboardingJsonDraft(value: OnboardingDraftValue | undefined): value is OnboardingJsonDraft {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

export function isOnboardingDraftValueEmpty(value: OnboardingDraftValue | undefined): boolean {
  if (value === undefined) return true;
  if (Array.isArray(value)) return value.length === 0;
  return (isOnboardingJsonDraft(value) ? value.json : value).trim().length === 0;
}

/** Columns whose draft differs from the seeded draft — only these are sent on edit. */
export function changedOnboardingColumns(
  draft: OnboardingDraft,
  initial: OnboardingDraft,
): OnboardingEditColumn[] {
  return ONBOARDING_EDIT_COLUMNS.filter(
    (column) => toComparable(draft[column]) !== toComparable(initial[column]),
  );
}

/** Compares what would be sent: a stored JSON draft by parsed value and empty tags as null. */
function toComparable(value: OnboardingDraftValue | undefined): string {
  if (Array.isArray(value)) return JSON.stringify(value.length > 0 ? value : null);
  if (!isOnboardingJsonDraft(value)) return JSON.stringify(value ?? null);
  return JSON.stringify(parseJsonDraft(value.json));
}

export interface ParsedOnboardingDraft {
  values: Partial<Record<OnboardingCreateColumn, unknown>>;
  errors: OnboardingFieldErrors;
}

/**
 * Turns draft strings into request values and flags what the API would
 * reject. Blank values become `null`; the API re-validates every value.
 */
export function parseOnboardingDraft(
  draft: OnboardingDraft,
  columns: readonly OnboardingCreateColumn[],
): ParsedOnboardingDraft {
  const values: ParsedOnboardingDraft["values"] = {};
  const errors: OnboardingFieldErrors = {};

  for (const column of columns) {
    const kind = onboardingInputKind(column);
    const candidate = toRequestValue(draft[column]);
    const parsed = CLIENT_CONTEXT_FIELDS[column].schema.safeParse(candidate);
    if (!parsed.success) {
      errors[column] = fieldErrorFor(column, kind, candidate);
      continue;
    }
    values[column] = parsed.data;
  }
  return { values, errors };
}

function toRequestValue(raw: OnboardingDraftValue | undefined): unknown {
  if (Array.isArray(raw)) return raw.length > 0 ? raw : null;
  if (isOnboardingJsonDraft(raw)) return parseJsonDraft(raw.json);
  const text = typeof raw === "string" ? raw.trim() : "";
  return text.length === 0 ? null : text;
}

/** The stored JSON of a read-only draft; it was produced by `JSON.stringify`, so it always parses. */
function parseJsonDraft(text: string): unknown {
  return text.trim().length === 0 ? null : JSON.parse(text);
}

function fieldErrorFor(column: OnboardingCreateColumn, kind: OnboardingInputKind, candidate: unknown): OnboardingFieldError {
  if (column === "website_url") return "invalidUrl";
  if (isStringListColumn(column) && candidate !== null && !isStringList(candidate)) return "invalidList";
  if (kind === "numeric") return "invalidNumber";
  return "invalid";
}

// ─── New Organization ───────────────────────────────────────────────

export type NewOrgOnboardingIssue =
  | "industryRequired"
  | "industrySpaces"
  | "partitionRequired"
  | "partitionMultiple"
  | "partitionInvalid"
  | "compRequired"
  | "compInvalid"
  | "fieldErrors"
  | "setupErrors";

export interface NewOrgOnboarding {
  customerPartition: string;
  idempotencyKey: string;
  compKey: string;
  fields: Record<string, unknown>;
  setup: ClientsParamsSetupInput;
}

/** New Organization onboarding and customer setup; the dialog skips them as a whole when onboarding is skipped. */
export interface NewOrgOnboardingPlan {
  /** The provisioning `onboarding` body; null until every required part is valid. */
  onboarding: NewOrgOnboarding | null;
  errors: OnboardingFieldErrors;
  setupErrors: CustomerSetupErrors;
  issue: NewOrgOnboardingIssue | null;
}

export function planNewOrgOnboarding(input: {
  customerPartition: string;
  clientIndustry: string;
  draft: OnboardingDraft;
  setupDraft: CustomerSetupDraft;
  idempotencyKey: string;
  compKey: string;
}): NewOrgOnboardingPlan {
  const industry = input.clientIndustry.trim();
  const { values, errors } = buildOnboardingCreateFields(input.draft, industry);
  const { setup, errors: setupErrors } = parseCustomerSetupDraft(input.setupDraft);
  const issue = newOrgOnboardingIssue(industry, input.customerPartition, input.compKey, errors, setupErrors);
  if (issue || !setup) return { onboarding: null, errors, setupErrors, issue };

  const onboarding = {
    customerPartition: input.customerPartition.trim(),
    idempotencyKey: input.idempotencyKey,
    compKey: input.compKey.trim(),
    fields: values,
    setup,
  };
  return rejectWhatTheApiWould(onboarding) ?? { onboarding, errors, setupErrors, issue: null };
}

/**
 * Last gate: the same two validators the API runs on the provisioning body. Anything they reject is
 * flagged on the form instead of reaching the server.
 */
function rejectWhatTheApiWould(onboarding: NewOrgOnboarding): NewOrgOnboardingPlan | null {
  const fields = validateOnboardingCreate(onboarding.fields);
  if (!fields.ok) {
    const column = fields.field;
    const errors: OnboardingFieldErrors = column && isClientContextColumn(column) ? { [column]: "invalid" } : {};
    return { onboarding: null, errors, setupErrors: {}, issue: "fieldErrors" };
  }
  const setup = validateClientsParamsSetup(onboarding.setup);
  if (!setup.ok) {
    return { onboarding: null, errors: {}, setupErrors: contractErrorFor(setup.message), issue: "setupErrors" };
  }
  return null;
}

function newOrgOnboardingIssue(
  industry: string,
  customerPartition: string,
  compKey: string,
  errors: OnboardingFieldErrors,
  setupErrors: CustomerSetupErrors,
): NewOrgOnboardingIssue | null {
  const customer = resolveClientContextCustomerName(customerPartition);
  if (!customer.ok) {
    return customer.code === ClientContextErrorCodes.MULTIPLE_CUSTOMER_NAMES ? "partitionMultiple" : "partitionRequired";
  }
  if (validateNewPartition(customer.customerName, "customerPartition")) return "partitionInvalid";
  if (!compKey.trim()) return "compRequired";
  if (validateNewPartition(compKey, "compKey")) return "compInvalid";
  if (!industry) return "industryRequired";
  if (hasClientIndustrySpaces(industry)) return "industrySpaces";
  if (Object.keys(errors).length > 0) return "fieldErrors";
  return Object.keys(setupErrors).length > 0 ? "setupErrors" : null;
}

/** Create body: non-empty columns only, so a partial form never writes explicit NULLs. */
export function buildOnboardingCreateFields(
  draft: OnboardingDraft,
  industry: string,
): ParsedOnboardingDraft {
  const filled = ONBOARDING_EDIT_COLUMNS.filter((column) => !isOnboardingDraftValueEmpty(draft[column]));
  return parseOnboardingDraft({ ...draft, industry }, ["industry", ...filled]);
}
